import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SEOHead } from '../../components/seo/SEOHead';
import { templateService, type Template } from '../../services/template.service';
import { LandingNav } from './LandingNav';
import { LandingFooter } from './LandingFooter';
import { BootOverlay, startBoot, clearBoot, type BootCopy } from './BootOverlay';
import { TemplateCard, TplArrow, TplBadge, UseButton, toTplCard } from './TemplateCard';
import { useApplyTemplate } from './useApplyTemplate';
import { useLandingTheme } from './useLandingTheme';
import { useLandingMotion } from './useLandingMotion';
import { FONT_MONO, FONT_SANS, FONT_SERIF } from './landingData';
import './landing.css';

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);
const eyebrow: React.CSSProperties = { fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hv-leaf)' };

type Sort = 'featured' | 'popular' | 'az';
const SORTS: { id: Sort; label: string }[] = [
    { id: 'featured', label: 'Featured' },
    { id: 'popular', label: 'Most used' },
    { id: 'az', label: 'A–Z' },
];

/** Lowercase, accents stripped: "Kafé" matches "kafe". */
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * /templates — the whole template catalog. Search, category and sort live in
 * the URL (?q=, ?category=, ?sort=) so a filtered view can be shared, and
 * ?template=<id> opens one template's details.
 */
export const TemplatesPage: React.FC = () => {
    const { user, isAuthenticated, logout } = useAuth();
    const [theme, toggleTheme] = useLandingTheme();
    const rootRef = useRef<HTMLDivElement>(null);
    useLandingMotion(rootRef);
    const [params, setParams] = useSearchParams();
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);

    const q = params.get('q') ?? '';
    const cat = params.get('category') ?? 'All';
    const sort = (SORTS.some((s) => s.id === params.get('sort')) ? params.get('sort') : 'featured') as Sort;
    const openId = params.get('template');

    const setParam = (key: string, value: string | null) => {
        const p = new URLSearchParams(params);
        if (value) p.set(key, value);
        else p.delete(key);
        setParams(p, { replace: key !== 'template', preventScrollReset: true });
    };

    useEffect(() => {
        void (async () => {
            const res = await templateService.list();
            if (res.success && res.data) setTemplates(res.data);
            setLoading(false);
        })();
    }, []);

    const [boot, setBoot] = useState<BootCopy | null>(null);
    const { applying, apply } = useApplyTemplate({
        isAuthenticated,
        launch: (copy) => { setBoot(copy); startBoot(copy); },
        abortLaunch: () => { setBoot(null); clearBoot(); },
    });
    const startFromTemplate = (id: string) => {
        const t = templates.find((x) => x.id === id);
        if (t) void apply(t);
    };

    const cats = useMemo(() => {
        const counts = new Map<string, number>();
        templates.forEach((t) => counts.set(t.category, (counts.get(t.category) ?? 0) + 1));
        return [['All', templates.length] as [string, number], ...Array.from(counts.entries())];
    }, [templates]);

    const results = useMemo(() => {
        const terms = norm(q).split(/\s+/).filter(Boolean);
        const list = templates.filter((t) => {
            if (cat !== 'All' && t.category !== cat) return false;
            if (!terms.length) return true;
            const hay = norm([t.name, t.description, t.category, ...t.tags].join(' '));
            return terms.every((term) => hay.includes(term));
        });
        if (sort === 'popular') return [...list].sort((a, b) => b.usageCount - a.usageCount);
        if (sort === 'az') return [...list].sort((a, b) => a.name.localeCompare(b.name, 'id'));
        return list;
    }, [templates, q, cat, sort]);

    const open = templates.find((t) => t.id === openId) ?? null;
    const filtered = q || cat !== 'All';

    return (
        <div ref={rootRef} className="hv-root" data-hv-theme={theme} style={{ position: 'static', fontFamily: FONT_SANS, color: 'var(--hv-ink)', background: 'var(--hv-desk)', overflowX: 'clip', minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
            <SEOHead
                title={open ? `${open.name} template` : cat !== 'All' ? `${cat} website templates` : 'Website templates'}
                description={open ? open.description : 'Ready-made website templates for small businesses, invitations, schools, events and portfolios. Pick one, make it yours by chatting with AI, and publish in minutes.'}
                url="https://hyvay.com/templates"
                canonicalUrl={cat !== 'All' ? `https://hyvay.com/templates?category=${encodeURIComponent(cat)}` : 'https://hyvay.com/templates'}
                keywords={['template website', 'template website umkm', 'template undangan online', 'website template indonesia', 'hyvay templates']}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'ItemList',
                    name: 'HyVay website templates',
                    numberOfItems: results.length,
                    itemListElement: results.map((t, i) => ({ '@type': 'ListItem', position: i + 1, name: t.name, description: t.description, url: `https://hyvay.com/templates?template=${t.id}` })),
                }}
            />

            <section data-screen-label="Templates hero" style={{ position: 'relative', overflow: 'hidden', background: 'var(--hv-page)' }}>
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.7, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.3px,transparent 1.9px)', backgroundSize: '24px 24px', maskImage: 'radial-gradient(ellipse 70% 50% at 50% 0%,black,transparent)', WebkitMaskImage: 'radial-gradient(ellipse 70% 50% at 50% 0%,black,transparent)' }} />
                <LandingNav theme={theme} onToggleTheme={toggleTheme} isAuthenticated={isAuthenticated} avatarUrl={user?.avatar} userName={user?.fullName} onLogout={logout} />

                <div className="hv-shell hv-hero-pad" style={{ position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '56px 28px 40px' }}>
                    <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
                        <div style={eyebrow}>Template library</div>
                        <h1 className="hv-h1" style={{ margin: '16px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 68, lineHeight: 1.0, letterSpacing: '-0.025em', color: 'var(--hv-ink)', ...tw('balance') }}>
                            Start from something <i>that already works</i>
                        </h1>
                        <p style={{ margin: '20px auto 0', maxWidth: 560, fontSize: 16.5, lineHeight: 1.6, color: 'var(--hv-body)', ...tw('pretty') }}>
                            {loading ? 'Finished sites' : `${templates.length} finished sites`} you can copy in one click, then make yours by chatting with the AI. Using a template costs no credits.
                        </p>
                    </div>

                    {/* Search + sort */}
                    <div style={{ display: 'flex', gap: 10, maxWidth: 720, margin: '30px auto 0', flexWrap: 'wrap' }}>
                        <label style={{ flex: 1, minWidth: 240, display: 'flex', alignItems: 'center', gap: 10, height: 50, padding: '0 16px', borderRadius: 999, border: '1.5px solid var(--hv-line)', background: 'var(--hv-card)', boxShadow: '0 20px 40px -34px var(--hv-shadow)' }}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--hv-muted)" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                            <input
                                type="search"
                                value={q}
                                onChange={(e) => setParam('q', e.target.value || null)}
                                placeholder="Search: kafe, undangan, sekolah…"
                                aria-label="Search templates"
                                style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', fontFamily: FONT_SANS, fontSize: 15, color: 'var(--hv-ink)' }}
                            />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 50, padding: '0 8px 0 16px', borderRadius: 999, border: '1.5px solid var(--hv-line)', background: 'var(--hv-card)', fontSize: 13, color: 'var(--hv-muted)' }}>
                            Sort
                            <select value={sort} onChange={(e) => setParam('sort', e.target.value === 'featured' ? null : e.target.value)} aria-label="Sort templates" style={{ height: 36, border: 0, outline: 'none', background: 'transparent', fontFamily: FONT_SANS, fontSize: 14, fontWeight: 700, color: 'var(--hv-ink)', cursor: 'pointer' }}>
                                {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                            </select>
                        </label>
                    </div>

                    {/* Categories */}
                    <div role="radiogroup" aria-label="Category" style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}>
                        {cats.map(([c, n]) => {
                            const active = c === cat;
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    data-track="templates_category"
                                    data-track-label={c}
                                    onClick={() => setParam('category', c === 'All' ? null : c)}
                                    className="hv-h-brand-bd"
                                    style={{ display: 'flex', alignItems: 'center', gap: 7, height: 36, padding: '0 14px 0 16px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: `1px solid ${active ? 'var(--hv-ink)' : 'var(--hv-line)'}`, background: active ? 'var(--hv-ink)' : 'var(--hv-card)', color: active ? 'var(--hv-page)' : 'var(--hv-body)' }}
                                >
                                    {c}
                                    <span style={{ fontFamily: FONT_MONO, fontSize: 10, opacity: 0.65 }}>{n}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section data-screen-label="Templates grid" className="hv-band" style={{ padding: '44px 0 88px', background: 'var(--hv-page)', borderTop: '1px solid var(--hv-line)' }}>
                <div className="hv-shell" style={{ maxWidth: 1280, margin: '0 auto', padding: '0 28px' }}>
                    {!loading && templates.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22, fontSize: 13.5, color: 'var(--hv-body)' }}>
                            <span><b style={{ color: 'var(--hv-ink)' }}>{results.length}</b> {results.length === 1 ? 'template' : 'templates'}{cat !== 'All' ? ` in ${cat}` : ''}{q ? ` matching “${q}”` : ''}</span>
                            {filtered && (
                                <button type="button" onClick={() => setParams(new URLSearchParams(sort === 'featured' ? {} : { sort }), { replace: true })} className="hv-h-underline" style={{ border: 0, padding: 0, background: 'transparent', fontFamily: FONT_SANS, fontSize: 13, fontWeight: 600, color: 'var(--hv-mint-ink)', cursor: 'pointer' }}>
                                    Clear filters
                                </button>
                            )}
                        </div>
                    )}

                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0', fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>LOADING TEMPLATES…</div>
                    ) : results.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '56px 28px', borderRadius: 18, border: '1.5px dashed var(--hv-line-2)', background: 'var(--hv-page-2)', textAlign: 'center' }}>
                            <div style={{ fontFamily: FONT_SERIF, fontSize: 28, letterSpacing: '-0.01em' }}>{templates.length ? 'No template matches that' : 'The template catalog is not available yet'}</div>
                            <p style={{ margin: 0, maxWidth: 420, fontSize: 14, lineHeight: 1.6, color: 'var(--hv-body)' }}>Describe the site you need instead, and HyVay builds it from scratch.</p>
                            <Link to="/" data-track="templates_describe" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 10, height: 44, padding: '0 20px', borderRadius: 999, background: 'var(--brand,#13422C)', color: '#FFFDF7', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>Describe your site <TplArrow /></Link>
                        </div>
                    ) : (
                        <div className="hv-cols-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
                            {results.map((t) => (
                                <TemplateCard key={t.id} t={toTplCard(t)} busyId={applying} onUse={startFromTemplate} onOpen={(id) => setParam('template', id)} />
                            ))}
                        </div>
                    )}

                    {/* Nothing fits */}
                    {!loading && results.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginTop: 48, padding: 28, borderRadius: 24, background: 'var(--hv-mint)' }}>
                            <div style={{ flex: 1, minWidth: 240 }}>
                                <h2 style={{ margin: 0, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 30, letterSpacing: '-0.015em', color: 'var(--hv-mint-ink)' }}>Don't see what you need?</h2>
                                <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.6, color: 'var(--hv-mint-ink)' }}>Describe your site in your own words and HyVay builds it from scratch.</p>
                            </div>
                            <Link to="/" data-track="templates_describe" className="hv-h-deep" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 46, padding: '0 20px', borderRadius: 999, background: 'var(--brand,#13422C)', color: '#FFFDF7', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
                                Describe your site <TplArrow />
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            <LandingFooter />

            {open && <TemplateDetails t={open} busyId={applying} onUse={startFromTemplate} onClose={() => setParam('template', null)} />}
            {boot && <BootOverlay copy={boot} dark={theme === 'dark'} />}
        </div>
    );
};

export default TemplatesPage;

/* -------------------------------------------------------------- details */

const TemplateDetails: React.FC<{ t: Template; busyId: string | null; onUse: (id: string) => void; onClose: () => void }> = ({ t, busyId, onUse, onClose }) => {
    const card = toTplCard(t);
    const closeRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        document.body.classList.add('hv-modal-open');
        closeRef.current?.focus();
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => { document.body.classList.remove('hv-modal-open'); window.removeEventListener('keydown', onKey); };
    }, [onClose]);

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(20,21,22,0.6)', backdropFilter: 'blur(5px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 16px', overflowY: 'auto' }}>
            <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`${t.name} template`} style={{ position: 'relative', width: '100%', maxWidth: 920, borderRadius: 26, overflow: 'hidden', background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 60px 90px -40px rgba(0,0,0,0.5)', animation: 'hvPop 0.35s cubic-bezier(0.2,0.9,0.24,1) both' }}>
                <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="hv-h-mint" style={{ position: 'absolute', zIndex: 2, top: 14, right: 14, width: 36, height: 36, borderRadius: 999, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', color: 'var(--hv-body)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
                </button>
                <div style={{ position: 'relative', aspectRatio: '16 / 10', background: 'var(--hv-page-2)', borderBottom: '1px solid var(--hv-line)' }}>
                    {card.img ? (
                        <img src={card.img} alt={`${t.name} preview`} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                    ) : (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', padding: 28, background: 'linear-gradient(150deg,var(--hv-mint),var(--hv-page-2))' }}>
                            <span style={{ fontFamily: FONT_SERIF, fontSize: 48, color: 'var(--hv-mint-ink)', opacity: 0.6 }}>{t.name}</span>
                        </div>
                    )}
                    {t.badge && <TplBadge badge={t.badge} />}
                </div>
                <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', padding: 28 }}>
                    <div style={{ flex: '1 1 380px', minWidth: 0 }}>
                        <div style={{ ...eyebrow, color: 'var(--hv-muted)' }}>{t.category}</div>
                        <h2 style={{ margin: '10px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 40, lineHeight: 1.05, letterSpacing: '-0.02em', color: 'var(--hv-ink)' }}>{t.name}</h2>
                        <p style={{ margin: '14px 0 0', fontSize: 15, lineHeight: 1.7, color: 'var(--hv-body)', ...tw('pretty') }}>{t.description}</p>
                        {t.tags.length > 0 && (
                            <div style={{ display: 'flex', gap: 6, marginTop: 18, flexWrap: 'wrap' }}>
                                {t.tags.map((k) => <span key={k} style={{ height: 26, display: 'flex', alignItems: 'center', padding: '0 11px', borderRadius: 999, background: 'var(--hv-page-2)', fontSize: 12, fontWeight: 500, color: 'var(--hv-body)' }}>{k}</span>)}
                            </div>
                        )}
                    </div>
                    <div style={{ flex: '0 1 260px', display: 'flex', flexDirection: 'column', gap: 12, padding: 20, borderRadius: 18, background: 'var(--hv-page-2)', alignSelf: 'flex-start' }}>
                        <UseButton id={t.id} busyId={busyId} onUse={onUse} label="Use this template" big />
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8, fontSize: 13, lineHeight: 1.5, color: 'var(--hv-body)' }}>
                            <li>✓ Free to start, no credits used</li>
                            <li>✓ Change texts, photos and colours by chatting</li>
                            <li>✓ Publish to your own address</li>
                        </ul>
                        <div style={{ fontSize: 12, color: 'var(--hv-muted)' }}>{card.uses}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};
